import { detectLocale, extractIntentChunks } from './chunker'
import { stableHash } from './embedding'
import { createWebMcpChunkPlan } from './planner'
import { getDefaultLocalIntentRanker, type LocalIntentRanker } from './ranker'
import type { IntentChunkAnalysis } from './types'

export interface IntentChunkAnalyzerOptions {
  ranker?: LocalIntentRanker
  topK?: number
}

export class IntentChunkAnalyzer {
  private readonly ranker: LocalIntentRanker
  private readonly topK: number

  constructor(options: IntentChunkAnalyzerOptions = {}) {
    this.ranker = options.ranker ?? getDefaultLocalIntentRanker()
    this.topK = options.topK ?? 3
  }

  async analyze(rawInput: string): Promise<IntentChunkAnalysis> {
    const input = rawInput.trim()
    if (!input) {
      return {
        status: 'empty',
        inputHash: '',
        locale: 'en',
        chunks: [],
        plan: null,
        summary: '',
      }
    }

    const chunks = extractIntentChunks(input)
    const enriched = await this.ranker.enrichChunks(chunks, this.topK)
    const plan = createWebMcpChunkPlan(input, enriched)
    return {
      status: 'ready',
      inputHash: stableHash(input),
      locale: detectLocale(input),
      chunks: enriched,
      plan,
      summary: summarizeIntentChunks(enriched),
    }
  }
}

let defaultAnalyzer: IntentChunkAnalyzer | null = null

export function getDefaultIntentChunkAnalyzer(): IntentChunkAnalyzer {
  defaultAnalyzer ??= new IntentChunkAnalyzer()
  return defaultAnalyzer
}

export async function analyzeIntentDraft(input: string): Promise<IntentChunkAnalysis> {
  return await getDefaultIntentChunkAnalyzer().analyze(input)
}

export function summarizeIntentChunks(chunks: IntentChunkAnalysis['chunks']): string {
  if (chunks.length === 0) return ''
  const labels = chunks
    .slice(0, 4)
    .map((chunk) => chunk.kind)
    .filter((kind, index, arr) => arr.indexOf(kind) === index)
  return labels.join(' + ')
}
